import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock3, Send } from "lucide-react";
import { LOGO } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70";
const label = "block text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2";

export default function RegularizationRequest() {
  const [form, setForm] = useState({ email: "", date: "", punch_type: "IN", time: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/regularization/request`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nx-navy flex items-center justify-center px-6 py-16" data-testid="regularization-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-12 w-auto mx-auto mb-6" />
          <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Employee Self-Service</p>
          <h1 className="font-marcellus text-3xl text-white mt-3">Attendance Correction Request</h1>
          <p className="text-sm text-slate-500 mt-3">Forgot to punch in or out? Biometric failed? Submit the correction here — HR will review and update your attendance.</p>
        </div>

        {done ? (
          <div className="border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="regularization-success">
            <CheckCircle2 size={32} className="text-emerald-300 mx-auto mb-4" />
            <p className="text-white text-lg">Request submitted</p>
            <p className="text-sm text-slate-400 mt-2">HR has been notified. You will receive an email once your correction is approved or if more details are needed.</p>
            <a href="/my-requests" className="inline-block mt-5 text-xs text-nx-goldlight hover:text-white transition-colors border border-nx-gold/40 px-4 py-2" data-testid="regularization-track-link">Track this request anytime → My Requests</a>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-7 space-y-5">
            <label className="block">
              <span className={label}>Your email registered with HR</span>
              <input type="email" required className={input} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="reg-email" />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className={label}>Date of the missed punch</span>
                <input type="date" required className={input} value={form.date} onChange={set("date")} data-testid="reg-date" />
              </label>
              <label className="block">
                <span className={label}>Missed punch</span>
                <select className={input} value={form.punch_type} onChange={set("punch_type")} data-testid="reg-punch-type">
                  <option value="IN">Check-in</option>
                  <option value="OUT">Check-out</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className={label}>Correct time</span>
              <input type="time" required className={input} value={form.time} onChange={set("time")} data-testid="reg-time" />
            </label>
            <label className="block">
              <span className={label}>Reason</span>
              <textarea required minLength={10} rows={3} className={input} value={form.reason} onChange={set("reason")} placeholder="e.g. Was at the ABC customer site for installation; forgot to punch out at 5:30 PM." data-testid="reg-reason" />
            </label>
            <button type="submit" disabled={busy} className="w-full bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3.5 transition-colors disabled:opacity-50" data-testid="reg-submit">
              <Send size={13} className="inline mr-1" /> {busy ? "Submitting…" : "Submit for Approval"}
            </button>
            <p className="text-[11px] text-slate-600 text-center"><Clock3 size={11} className="inline mr-1" /> Corrections older than 31 days: please contact HR directly.</p>
          </form>
        )}
      </div>
    </div>
  );
}
